import {GridColDef, GridFilterModel} from "@mui/x-data-grid";
import {useEffect, useMemo} from "react";
import {useGeneCellDataFilters} from "../components/list/GeneCellDataFilters";
import {useListContext} from "../contexts/ListContext";
import {Correlation, Correlations, Score, Scores} from "../types/list";

type FilterableArgs =
  | { data: Correlations | undefined; columnDef: GridColDef<Correlation, any, any>[] }
  | { data: Scores | undefined; columnDef: GridColDef<Score, any, any>[] };

export const useFilteredTableData = (
  {data, columnDef}: FilterableArgs,
  filterModel: GridFilterModel | undefined,
) => {
  const {setDisplayedTableData} = useListContext();
  const geneCellDataFilters = useGeneCellDataFilters();

  const operatorLookup = useMemo(() =>
    geneCellDataFilters.reduce<Record<string, any>>(
      (acc, op) => ({...acc, [op.value]: op}),
      {}
    ), [geneCellDataFilters]);

  const columnDefLookup: Record<string, GridColDef> = useMemo(() =>
    (columnDef as GridColDef[]).reduce((acc, col) => ({...acc, [col.field]: col}), {}), [columnDef]);

  const filter = (row: Score | Correlation) =>
    filterModel?.items.every(f => {
      if (!f.value) return true;

      const col = columnDefLookup[f.field];
      if (!col) return true;

      const operator = operatorLookup[f.operator];
      if (!operator) return true;

      const applyFn = operator.getApplyFilterFn(f, col);
      if (!applyFn) return true;

      return applyFn({row, field: f.field});
    })

  useEffect(() => {
    if (!data) {
      setDisplayedTableData(null);
      return;
    }

    if (!filterModel || filterModel.items.length === 0) {
      setDisplayedTableData(data);
      return;
    }

    if ("correlations" in data) {
      setDisplayedTableData({correlations: data.correlations.filter(filter)});
    } else {
      setDisplayedTableData({scores: data.scores.filter(filter)});
    }
  }, [data, filterModel, setDisplayedTableData]);
};